<template>
  <div class="card" :class="`s-${status}`" @click="openDish(dish.id)">
    <div class="thumb" :style="thumbStyle">{{ initials }}</div>

    <div class="body">
      <div class="name">{{ name }}</div>

      <div v-if="showBanner" class="banner" :class="`s-${status}`">
        <span class="dot" />
        <span>{{ bannerText }}</span>
      </div>

      <div v-if="noAllergens" class="no-alg">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        {{ ui.sinAlergenos }}
      </div>

      <div v-if="badges.length" class="badges">
        <span
          v-for="(b, i) in badges"
          :key="i"
          class="badge"
          :class="{ traza: b.isTraza, mine: b.isMine }"
          :title="b.title"
        >
          <allergen-image :id="b.id" :size="22" />
        </span>
      </div>
    </div>

    <svg class="chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sand-400)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6" /></svg>
  </div>
</template>

<script>
import { PAL, UI, resolveAllergen, initialsOf } from '@/data'
import { dishStatus, bannerFor } from '@/viewmodel'
import { store, openDish } from '@/store'
import AllergenImage from './ui/AllergenImage.vue'

export default {
  name: 'DishCard',
  components: { AllergenImage },
  props: {
    dish: { type: Object, required: true }
  },
  data() {
    return { store }
  },
  computed: {
    ui() { return UI[store.locale] || UI.es },
    name() { return this.dish.nombre || '—' },
    initials() { return initialsOf(this.dish.nombre) },
    palette() { return PAL[this.dish.id % 4] },
    thumbStyle() {
      return { background: this.palette[0], color: this.palette[1] }
    },
    algIds() { return this.dish.alergeno_ids || [] },
    tzIds() { return this.dish.traza_ids || [] },
    status() {
      return dishStatus(this.algIds, this.tzIds, store.mine)
    },
    badges() {
      const list = []
      this.algIds.forEach(id => {
        const a = resolveAllergen(id, store.locale, store.alergenosMap)
        list.push({ id, title: a.name, isTraza: false, isMine: store.mine.includes(id) })
      })
      this.tzIds.forEach(id => {
        const a = resolveAllergen(id, store.locale, store.alergenosMap)
        list.push({ id, title: `${a.name} (${this.ui.trazasT})`, isTraza: true, isMine: store.mine.includes(id) })
      })
      return list
    },
    noAllergens() { return this.algIds.length === 0 && this.tzIds.length === 0 },
    showBanner() { return this.status !== 'neutral' },
    bannerText() {
      return bannerFor(this.status, this.algIds, this.tzIds, store.mine, store.locale, store.alergenosMap)
    }
  },
  methods: { openDish }
}
</script>

<style scoped>
.card {
  display: flex;
  gap: 14px;
  padding: 14px;
  background: #fff;
  border: 1px solid #E8E1D5;
  border-left: 4px solid #E8E1D5;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(20, 36, 46, 0.06);
  cursor: pointer;
  align-items: flex-start;
  transition: all 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
.card:hover {
  box-shadow: 0 8px 24px rgba(20, 36, 46, 0.12);
  transform: translateY(-2px);
}
.card.s-danger { border-left-color: #B23A3A; }
.card.s-warning { border-left-color: #C4862A; }
.card.s-safe { border-left-color: #00A47A; }
.card.s-neutral { border-left-color: #E8E1D5; }

.thumb {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.2rem;
}

.body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.name {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--seth-navy);
  line-height: 1.2;
}

.banner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
}
.banner .dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.banner.s-danger { color: #B23A3A; }
.banner.s-warning { color: #C4862A; }
.banner.s-safe { color: #00805F; }

.no-alg {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--seth-green-700);
}

.badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #E8E1D5;
}
.badge.mine { border: 2px solid #B23A3A; }
.badge.traza { border: 1px dashed #D6CCBC; }
.badge.traza.mine { border: 2px dashed #C4862A; }

.chevron { flex-shrink: 0; margin-top: 4px; }
</style>
